import { FC, useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Button,
  Column,
  EmptyState,
  IconButton,
  RefreshIcon,
  Row,
  SearchInput,
  Skeleton,
  SkeletonBox,
  Text,
  Tooltip,
} from "@hightouch/htui";
import { Link } from "src/router";
import { matchSorter } from "match-sorter";

import { useDestinationForm } from "src/contexts/destination-form-context";

import { useFormkitContext } from "src/formkit/components/formkit-context";
import {
  FormProps,
  MappingType,
  JsonColumnProps,
} from "src/formkit/components/types";
import { extractColumnValues } from "src/formkit/components/utils";
import {
  extractEligibleInlineMapperColumns,
  isValidJsonColumnMetadata,
} from "./utils";

const rowSx = {
  minHeight: "32px",
  alignItems: "center",
  fontSize: "10px",
  gap: 2,
  paddingTop: 1,
  px: 2,
};

interface OnChangeValueProp {
  from: string;
  type: "array";
}

type ArrayInputProps = {
  onClose: () => void;
  jsonColumnProperties: JsonColumnProps;
  onChange: (onClose: () => void, value: OnChangeValueProp) => void;
  onChangeJsonColumnProperties: React.Dispatch<
    React.SetStateAction<JsonColumnProps>
  >;
};

export const ArrayInput: FC<Readonly<FormProps & ArrayInputProps>> = ({
  onClose,
  columnOptions,
  jsonColumnProperties,
  onChange,
  onChangeJsonColumnProperties,
  onReloadEligibleInlineMapperColumns,
  value,
}) => {
  const [search, setSearch] = useState("");
  const selectedColumnRef = useRef<HTMLDivElement>(null);
  const { reloadRows, loadingRows, rows } = useDestinationForm();
  const { columns: contextColumns, model } = useFormkitContext();
  const isEventModel = Boolean(model?.isEventModel);
  const allColumns = columnOptions ?? contextColumns;

  const hasAnySuggestedArrayColumns = useMemo(() => {
    if (!isEventModel) return true;
    const allOptions = (allColumns ?? []).flatMap(
      (group) => group.options ?? [],
    );
    return allOptions.some((o) => o?.type === "array");
  }, [allColumns, isEventModel]);

  const arrayOptions = useMemo(() => {
    if (!isEventModel) return [];
    const values = extractColumnValues(
      allColumns,
      (opt) => opt?.type === "array",
    );
    // matchSorter expects an array
    return Array.from(values);
  }, [allColumns, isEventModel]);

  const columns = useMemo(() => {
    if (isEventModel) {
      if (search) {
        return matchSorter(arrayOptions, search);
      }
      return arrayOptions;
    }

    const arrayColumns = Object.keys(
      jsonColumnProperties?.allColumnsProps || {},
    );
    if (search) {
      return matchSorter(arrayColumns, search);
    }

    return arrayColumns;
  }, [
    arrayOptions,
    isEventModel,
    jsonColumnProperties.allColumnsProps,
    search,
  ]);

  const shouldShowCreateNewFieldOption = useMemo(() => {
    if (!isEventModel) return false;
    const trimmed = search.trim();
    if (!trimmed) return false;
    // Column names are case-sensitive as they match event property names
    const exactMatch = columns.some((c) => c === trimmed);
    return !exactMatch;
  }, [columns, isEventModel, search]);

  useEffect(() => {
    selectedColumnRef.current?.scrollIntoView();
  }, []);

  const handleReloadRows = () => {
    onReloadEligibleInlineMapperColumns(value.from);
  };

  /**
   * These two useEffects work together to handle two functionalities:
   * 1. On load, we want to check if the json column metadata is valid. We will reloadRows to validate the metadata if it is not.
   * 2. handleReloadRows, which calls onReloadEligibleInlineMapperColumns will load the metadata to state only if it is valid. Therefore, it needs to have 'rows' as a dependency
   *    because if rows is updated, it can mean that either 1. we loaded the new valid metadata after we found that the previous one is invalid. 2. The user clicked on the 'Reload button' to re-validate the metadata and we need to extract that new data and set it to state.
   * This approach is needed to avoid multiple re-renders. The ideal approach would've been to make `reloadRows` a promise that resolves with the row data and we preform the extraction afterwards. However, that was not how reloadRows worked at this time, so this approach was chosen for the sake of time.
   * If this should be revisited, reloadRows should be changed into a promise so it can handle this scenario without multiple useEffects.
   */

  /**
   * These two useEffects work together to handle JSON column metadata:
   * 1. On mount/column changes, check if metadata is valid. If not, trigger a reload.
   * 2. When rows are updated (from reload), extract the new metadata and set it to state.
   *
   * This approach avoids multiple re-renders. Ideally, reloadRows would return a promise
   * with the data, but the current implementation uses this two-step pattern.
   */
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (isEventModel) return;
    const jsonColumns = extractEligibleInlineMapperColumns(allColumns);
    if (!isValidJsonColumnMetadata(jsonColumns)) {
      reloadRows();
    }
    // Only run on mount and when allColumns changes
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression
  }, [allColumns, isEventModel]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (isEventModel) return;
    handleReloadRows();
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression
  }, [rows, isEventModel]);

  return (
    <Column flex={1} minHeight="0" p={3}>
      <Box pb={2}>
        <Text>Select a column that contains an array of objects:</Text>
      </Box>
      <Row alignItems="center" justifyContent="space-between" pb={2}>
        <>
          <SearchInput
            autoFocus
            placeholder={
              isEventModel && !hasAnySuggestedArrayColumns
                ? "Enter an expected array column..."
                : "Search..."
            }
            value={search}
            width="100%"
            onChange={(event) => setSearch(event.target.value)}
          />
          <Box ml={2}>
            <Tooltip
              message={isEventModel ? "Refresh schema" : "Refresh properties"}
            >
              <IconButton
                aria-label={
                  isEventModel ? "Refresh schema" : "Refresh array properties"
                }
                icon={RefreshIcon}
                isLoading={loadingRows}
                size="md"
                variant="secondary"
                onClick={reloadRows}
              />
            </Tooltip>
          </Box>
        </>
      </Row>
      {loadingRows && !isEventModel ? (
        <ShimmerLoadingState />
      ) : !columns?.length &&
        !shouldShowCreateNewFieldOption &&
        !isEventModel ? (
        <Row
          flex={1}
          height="100%"
          sx={{
            div: {
              justifyContent: "center",
              p: 0,
              width: "100%",
            },
          }}
          width="100%"
        >
          <EmptyState
            actions={
              <Button variant="primary" onClick={reloadRows}>
                Reload columns
              </Button>
            }
            message={
              <>
                Reference our{" "}
                <Link href="https://hightouch.com/docs/syncs/mapping-data#required-data-format">
                  documentation
                </Link>{" "}
                to find out about the required data format to use the array
                in-line mapper.
              </>
            }
            title="No eligible columns found"
          />
        </Row>
      ) : (
        <Box flex="1" overflowY="auto">
          {shouldShowCreateNewFieldOption && (
            <Row
              key="__create__"
              _hover={{
                bg: "base.background",
              }}
              alignItems="center"
              borderBottomWidth="hairline"
              borderBottomStyle="solid"
              borderColor="base.border"
              cursor="pointer"
              flex="0"
              gap={2}
              minHeight={8}
              px={2}
              onClick={() => {
                const created = search.trim();
                if (!created) return;
                onChangeJsonColumnProperties((currentValue) => ({
                  ...currentValue,
                  selectedColumnProps: undefined,
                }));
                onChange(onClose, { type: MappingType.ARRAY, from: created });
              }}
            >
              <Box
                maxWidth="fit-content"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                <Text>
                  Select "{search.trim()}" as an expected array column...
                </Text>
              </Box>
            </Row>
          )}
          {(columns || []).map((column) => {
            // Both `column` and `value.from` are column-name strings here, so
            // identity equality is the right selection check.
            const selected = column === value.from;
            return (
              <Row
                key={column}
                ref={selected ? selectedColumnRef : undefined}
                _hover={{
                  bg: "base.background",
                }}
                alignItems="center"
                bg={selected ? "primary.background" : undefined}
                borderBottomWidth="hairline"
                borderBottomStyle="solid"
                borderColor="base.border"
                cursor="pointer"
                flex="0"
                gap={2}
                minHeight={8}
                pointerEvents={selected ? "none" : undefined}
                px={2}
                onClick={() => {
                  if (!isEventModel) {
                    onChangeJsonColumnProperties((currentValue) => ({
                      ...currentValue,
                      selectedColumnProps:
                        currentValue.allColumnsProps?.[column],
                    }));
                  }
                  onChange(onClose, { type: MappingType.ARRAY, from: column });
                }}
              >
                <Box
                  maxWidth="fit-content"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  <Text color={selected ? "primary.base" : undefined}>
                    {column}
                  </Text>
                </Box>
                <Box
                  maxWidth="fit-content"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {!isEventModel && (
                    <Text color="text.placeholder">
                      {Array.from(
                        jsonColumnProperties.allColumnsProps?.[column] || [],
                      ).join(", ")}
                    </Text>
                  )}
                </Box>
              </Row>
            );
          })}
        </Box>
      )}
    </Column>
  );
};

const containers = [
  ["25%", "75%"],
  ["20%", "80%"],
  ["35%", "85%"],
  ["35%", "65%"],
  ["25%", "95%"],
  ["20%", "35%"],
  ["15%", "75%"],
];

const ShimmerLoadingState = () => (
  <Skeleton isLoading={true}>
    {containers.map((container, index) => (
      <Row key={index} sx={rowSx}>
        <Box m={0} width={container[0]}>
          <SkeletonBox borderRadius="sm" height="18px" width="100%" />
        </Box>
        <Box m={0} width="100%">
          <SkeletonBox borderRadius="sm" height="18px" width={container[1]} />
        </Box>
      </Row>
    ))}
  </Skeleton>
);
